import { useState, useEffect } from 'react'
import { useParams, useNavigate } from 'react-router-dom'
import { VStack, HStack, Text, Button, Skeleton, Card, Heading, Divider } from 'wss3-forge'
import {
  ArrowLeft20Regular,
  ArrowRight20Regular
} from '@fluentui/react-icons'
import { MarkdownRenderer } from '../components/MarkdownRenderer'
import { DocsTableOfContents } from '../components/DocsTableOfContents'
import { useDocStructure } from '../components/DocsLayout'
import { getDocPage, DocPage } from '../lib/docs'

export function DocsPageView() {
  const { pluginSlug, sectionSlug, pageSlug } = useParams()
  const navigate = useNavigate()
  const { structure } = useDocStructure()
  const [page, setPage] = useState<DocPage | null>(null)
  const [loading, setLoading] = useState(true)
  const [showToc, setShowToc] = useState(window.innerWidth >= 1200)

  useEffect(() => {
    const handleResize = () => {
      setShowToc(window.innerWidth >= 1200)
    }
    window.addEventListener('resize', handleResize)
    return () => window.removeEventListener('resize', handleResize)
  }, [])

  useEffect(() => {
    if (pluginSlug && sectionSlug && pageSlug) {
      loadPage()
    }
  }, [pluginSlug, sectionSlug, pageSlug])

  async function loadPage() {
    if (!pluginSlug || !sectionSlug || !pageSlug) return

    try {
      setLoading(true)
      const data = await getDocPage(pluginSlug, sectionSlug, pageSlug)
      setPage(data)
    } catch (err) {
      console.error('Failed to load page:', err)
      setPage(null)
    } finally {
      setLoading(false)
    }
  }

  const navigation = (() => {
    if (!structure) return { prev: null, next: null }

    const allPages: Array<{ sectionSlug: string; pageSlug: string; title: string }> = []
    structure.sections.forEach(section => {
      section.pages?.forEach(p => {
        allPages.push({
          sectionSlug: section.slug,
          pageSlug: p.slug,
          title: p.title
        })
      })
    })

    const currentIndex = allPages.findIndex(
      p => p.sectionSlug === sectionSlug && p.pageSlug === pageSlug
    )

    return {
      prev: currentIndex > 0 ? allPages[currentIndex - 1] : null,
      next: currentIndex < allPages.length - 1 ? allPages[currentIndex + 1] : null
    }
  })()

  if (loading) {
    return (
      <div style={{ display: 'flex', padding: '48px 32px', maxWidth: 1200, margin: '0 auto' }}>
        <div style={{ flex: 1, maxWidth: 800 }}>
          <VStack gap="lg">
            <Skeleton width={200} height={16} />
            <Skeleton width={400} height={40} />
            <Skeleton width="100%" height={20} />
            <Skeleton width="100%" height={300} />
          </VStack>
        </div>
      </div>
    )
  }

  if (!page) {
    return (
      <div style={{ padding: 48, textAlign: 'center' }}>
        <VStack gap="md" align="center">
          <Heading level={2}>Page not found</Heading>
          <Text color="muted">The requested page does not exist or is not published.</Text>
          <Button
            variant="secondary"
            onClick={() => navigate(`/${pluginSlug}`)}
          >
            Go to documentation home
          </Button>
        </VStack>
      </div>
    )
  }

  return (
    <div style={{
      display: 'flex',
      gap: 48,
      padding: '64px 32px',
      maxWidth: 1200,
      margin: '0 auto'
    }}>
      {/* Main Content */}
      <article style={{ flex: 1, maxWidth: showToc ? 800 : '100%', minWidth: 0 }}>
        <VStack gap="xs">
          {/* Page Header */}
          <VStack gap="sm">
            <Text size="sm" color="muted" style={{ textTransform: 'uppercase', letterSpacing: '0.05em' }}>
              {structure?.sections.find(s => s.slug === sectionSlug)?.title}
            </Text>
            <Heading level={1}>{page.title}</Heading>
            {page.short_description && (
              <Text color="muted" size="lg">{page.short_description}</Text>
            )}
          </VStack>

          <Divider />

          {/* Content */}
          <div>
            <MarkdownRenderer content={page.content_md} />
          </div>

          {/* Navigation Cards */}
          <HStack gap="md" style={{ justifyContent: 'space-between', marginTop: 32 }}>
            {navigation.prev ? (
              <Card
                hoverable
                padding="md"
                onClick={() => navigate(`/${pluginSlug}/${navigation.prev!.sectionSlug}/${navigation.prev!.pageSlug}`)}
                style={{ flex: 1, maxWidth: '45%' }}
              >
                <HStack gap="sm" align="center">
                  <ArrowLeft20Regular style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                  <VStack gap="xs" style={{ minWidth: 0 }}>
                    <Text size="xs" color="muted">Previous</Text>
                    <Text size="sm" style={{ fontWeight: 500 }}>{navigation.prev.title}</Text>
                  </VStack>
                </HStack>
              </Card>
            ) : <div />}

            {navigation.next ? (
              <Card
                hoverable
                padding="md"
                onClick={() => navigate(`/${pluginSlug}/${navigation.next!.sectionSlug}/${navigation.next!.pageSlug}`)}
                style={{ flex: 1, maxWidth: '45%', marginLeft: 'auto' }}
              >
                <HStack gap="sm" align="center" style={{ justifyContent: 'flex-end' }}>
                  <VStack gap="xs" style={{ minWidth: 0, alignItems: 'flex-end' }}>
                    <Text size="xs" color="muted">Next</Text>
                    <Text size="sm" style={{ fontWeight: 500 }}>{navigation.next.title}</Text>
                  </VStack>
                  <ArrowRight20Regular style={{ color: 'var(--text-muted)', flexShrink: 0 }} />
                </HStack>
              </Card>
            ) : <div />}
          </HStack>

          {/* Footer */}
          <Divider />
          <Text size="sm" color="muted">
            Last updated: {new Date(page.updated_at).toLocaleDateString('en-US', {
              year: 'numeric',
              month: 'long',
              day: 'numeric'
            })}
          </Text>
        </VStack>
      </article>

      {/* Table of Contents */}
      {showToc && <DocsTableOfContents content={page.content_md} />}
    </div>
  )
}
